import type { DriveStep } from 'driver.js';
import type { UserPreferences, UserGuidedTourProgress } from '~/types/models';

export const WORKSPACE_WELCOME_TOUR_ID = 'workspace-welcome';
export const WORKSPACE_WELCOME_TOUR_VERSION = 1;

export function workspaceTourProgressKey(tenantId: string, tourId: string): string {
    return `${String(tenantId || '').trim()}:${String(tourId || '').trim()}`;
}

export function getWorkspaceTourProgress(
    preferences: UserPreferences | null | undefined,
    tenantId: string,
    tourId = WORKSPACE_WELCOME_TOUR_ID,
): UserGuidedTourProgress | null {
    const key = workspaceTourProgressKey(tenantId, tourId);
    return preferences?.guidedTours?.[key] || null;
}

export function shouldOfferWorkspaceWelcomeTour(
    preferences: UserPreferences | null | undefined,
    tenantId: string,
): boolean {
    return shouldOfferGuidedTour(
        preferences,
        tenantId,
        WORKSPACE_WELCOME_TOUR_ID,
        WORKSPACE_WELCOME_TOUR_VERSION,
    );
}

export function shouldOfferGuidedTour(
    preferences: UserPreferences | null | undefined,
    tenantId: string,
    tourId: string,
    version: number,
): boolean {
    if (!String(tenantId || '').trim() || !String(tourId || '').trim()) return false;

    const progress = getWorkspaceTourProgress(preferences, tenantId, tourId);
    return !progress || progress.version < version;
}

type WorkspaceWelcomeTourStepOptions = {
    mobile: boolean;
    workspaceName: string;
};

export function createWorkspaceWelcomeTourSteps({
    mobile,
    workspaceName,
}: WorkspaceWelcomeTourStepOptions): DriveStep[] {
    const name = String(workspaceName || '').trim() || 'your workspace';

    if (mobile) {
        return [
            {
                popover: {
                    title: `Welcome to ${name}`,
                    description: 'Here is a quick introduction to the controls that help you move around Vuey and start work without losing your place.',
                },
            },
            {
                element: '[data-tour="workspace-mobile-navigation"]',
                popover: {
                    title: 'Your workspace menu',
                    description: 'Open this menu to reach clients, projects, tasks, messages, processes, revenue, and settings.',
                    side: 'top',
                    align: 'center',
                },
            },
            {
                element: '[data-tour="workspace-command-menu"]',
                popover: {
                    title: 'Search the whole workspace',
                    description: 'Search for a page, client, project, or task from anywhere in the app.',
                    side: 'bottom',
                    align: 'center',
                },
            },
            {
                element: '[data-tour="workspace-notifications"]',
                popover: {
                    title: 'Keep up with activity',
                    description: 'Notifications bring approvals, client responses, deadlines, and other important workspace activity together.',
                    side: 'bottom',
                    align: 'end',
                },
            },
            {
                popover: {
                    title: 'You are ready to explore',
                    description: 'Open the mobile menu whenever you want to move to another area. You can restart this tour later from the account section of that menu.',
                },
            },
        ];
    }

    return [
        {
            popover: {
                title: `Welcome to ${name}`,
                description: 'Here is a quick introduction to the controls that help you move around Vuey and start work without losing your place.',
            },
        },
        {
            element: '[data-tour="workspace-navigation"]',
            popover: {
                title: 'Everything starts here',
                description: 'Use the workspace navigation to move between daily work, client processes, tools, revenue, and settings.',
                side: 'right',
                align: 'start',
            },
        },
        {
            element: '[data-tour="workspace-command-menu"]',
            popover: {
                title: 'Jump to anything',
                description: 'Search for pages, clients, projects, and tasks. You can also press Cmd or Ctrl + K whenever you need it.',
                side: 'bottom',
                align: 'center',
            },
        },
        {
            element: '[data-tour="workspace-quick-actions"]',
            popover: {
                title: 'Create without losing context',
                description: 'Quick actions let you add a task, project, client, or quote from wherever you are working.',
                side: 'bottom',
                align: 'end',
            },
        },
        {
            element: '[data-tour="nav-clients"]',
            popover: {
                title: 'Clients connect the work',
                description: 'A client brings communication, projects, onboarding, documents, portal access, and commercial history into one place.',
                side: 'right',
                align: 'center',
            },
        },
        {
            element: '[data-tour="workspace-account-menu"]',
            popover: {
                title: 'Settings and help stay close',
                description: 'Use your account menu to reach settings or restart this workspace tour whenever you want a refresher.',
                side: 'top',
                align: 'start',
            },
        },
        {
            popover: {
                title: 'You are ready to explore',
                description: 'Start with Clients when you want to add real work, or use workspace search to jump directly to the area you need.',
            },
        },
    ];
}
